import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { debounceTime, distinctUntilChanged, map, of, switchMap } from 'rxjs';
import {
  SurveyVariableConditionFilterCompanyProductScopeTypeEnum,
  SurveyVariableConditionFilterCompanyProductScopeTypeEnumLabels,
  SurveyVariableConditionFilterCompanyProductScopeTypeEnumOptions,
} from 'src/app/enums/survey-variable-condition-filter-company-product-scope-type.enum';
import { SurveyVariableConditionFilterDirectionEnum } from 'src/app/enums/survey-variable-condition-filter-direction.enum';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { CompanyProduct } from 'src/app/models/company_product.model';
import { SurveyFilter } from 'src/app/models/survey.model';
import { ApiService } from 'src/app/services';

export type TargetedCompanyProductOption = Pick<CompanyProduct, 'id' | 'title' | 'code'>;

@Component({
  selector: 'app-targeted-company-products-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './targeted-company-products-card.component.html',
  styleUrl: './targeted-company-products-card.component.css',
  host: { '[attr.title]': 'null' },
  imports: [ReactiveFormsModule, MatRadioModule, MatFormFieldModule, MatInputModule, MatChipsModule, MatAutocompleteModule, MatIconModule],
})
export class TargetedCompanyProductsCardComponent {
  private readonly api_service = inject(ApiService);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly search_input = viewChild<ElementRef<HTMLInputElement>>('search_input');

  readonly title = input('Productos');
  readonly description = input<string | null>(null);
  readonly embedded = input(false);
  readonly error_message = input<string | null>(null);
  readonly scope = model.required<SurveyVariableConditionFilterCompanyProductScopeTypeEnum>();
  readonly selection = model.required<TargetedCompanyProductOption[]>();
  readonly products_blur = output<void>();

  protected readonly scope_options = SurveyVariableConditionFilterCompanyProductScopeTypeEnumOptions;
  protected readonly scope_labels = SurveyVariableConditionFilterCompanyProductScopeTypeEnumLabels;
  protected readonly manual_scope = SurveyVariableConditionFilterCompanyProductScopeTypeEnum.Manual;
  protected readonly search_control = new FormControl('', { nonNullable: true });
  protected readonly suggestions = signal<TargetedCompanyProductOption[]>([]);
  protected readonly is_manual = computed(() => this.scope() === this.manual_scope);
  protected readonly selected_ids_set = computed(() => new Set(this.selection().map(product => product.id)));
  protected readonly display_search_value = (value: string | TargetedCompanyProductOption | null): string =>
    typeof value === 'string' ? value : '';

  constructor() {
    this.search_control.valueChanges
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap(raw_term => {
          const term = raw_term.trim();

          if (term.length < 2) {
            return of([] as TargetedCompanyProductOption[]);
          }

          return this.api_service
            .getCompanyProducts({
              search: term,
              scopes: 'isActive',
              limit: 20,
            })
            .pipe(
              map((response: ArrayResponse<CompanyProduct>): TargetedCompanyProductOption[] => {
                const selected_ids = this.selected_ids_set();

                return (response.data ?? [])
                  .filter(product => !selected_ids.has(product.id))
                  .map(product => ({ id: product.id, title: product.title, code: product.code }));
              })
            );
        }),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe(products => {
        this.suggestions.set(products);
      });
  }

  protected onScopeChange(scope: SurveyVariableConditionFilterCompanyProductScopeTypeEnum): void {
    this.scope.set(scope);

    if (scope !== this.manual_scope) {
      this.selection.set([]);
      this.search_control.setValue('');
      this.suggestions.set([]);

      return;
    }

    afterNextRender(() => this.search_input()?.nativeElement.focus(), { injector: this.injector });
  }

  protected onProductSelected(event: MatAutocompleteSelectedEvent): void {
    const product = event.option.value as TargetedCompanyProductOption;

    if (!this.selected_ids_set().has(product.id)) {
      this.selection.set([...this.selection(), product]);
    }

    this.suggestions.set([]);
  }

  protected onSearchPanelClosed(): void {
    this.search_control.setValue('');
    const input = this.search_input()?.nativeElement;
    if (input) {
      input.value = '';
    }
  }

  protected removeProduct(product_id: number): void {
    this.selection.set(this.selection().filter(product => product.id !== product_id));
  }

  protected onProductsInputBlur(event: FocusEvent): void {
    const next_focus = event.relatedTarget;

    if (next_focus instanceof HTMLElement && next_focus.closest('.mat-mdc-autocomplete-panel, .mat-autocomplete-panel')) {
      return;
    }

    this.products_blur.emit();
  }

  protected displayProductLabel(product: TargetedCompanyProductOption): string {
    return product.code ? `${product.title} (${product.code})` : product.title;
  }
}

export function getToAskAboutCompanyProductScope(filters: SurveyFilter[]): SurveyVariableConditionFilterCompanyProductScopeTypeEnum {
  const filter = filters.find(item => item.direction === SurveyVariableConditionFilterDirectionEnum.ToAskAbout);

  return filter?.company_product_scope_type ?? SurveyVariableConditionFilterCompanyProductScopeTypeEnum.None;
}

export function getToAskAboutCompanyProducts(filters: SurveyFilter[]): TargetedCompanyProductOption[] {
  const filter = filters.find(item => item.direction === SurveyVariableConditionFilterDirectionEnum.ToAskAbout);

  if (filter?.company_product_scope_type !== SurveyVariableConditionFilterCompanyProductScopeTypeEnum.Manual) {
    return [];
  }

  return (filter.company_products ?? []).map(product => ({
    id: product.id,
    title: product.title,
    code: product.code,
  }));
}

export function setTargetedCompanyProductSelection(
  scope_control: FormControl<SurveyVariableConditionFilterCompanyProductScopeTypeEnum>,
  products_control: FormControl<TargetedCompanyProductOption[]>,
  scope: SurveyVariableConditionFilterCompanyProductScopeTypeEnum,
  products: TargetedCompanyProductOption[]
): void {
  scope_control.setValue(scope);
  products_control.setValue(scope === SurveyVariableConditionFilterCompanyProductScopeTypeEnum.Manual ? products : []);
  scope_control.markAsDirty();
  scope_control.markAsTouched();
  products_control.markAsDirty();
}
